import { useEffect, useMemo, useState, type CSSProperties, type ReactNode } from 'react'
import { useSearchParams } from 'react-router'
import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query'
import {
  Button,
  DiffViewer,
  EmptyState,
  ErrorBanner,
  FrmIcon,
  Pill,
  SearchInput,
  Skeleton,
  StatusPill,
  Textarea,
  WorkbenchPanel,
  useToast,
  type DiffLine,
} from '@frm/ui'

import {
  confirmSkill,
  diffSkill,
  fetchSkillHistory,
  fetchSkills,
  type SkillDto,
  type SkillType,
} from '../api/skills'

const SKILL_TYPES: Array<{ id: SkillType; label: string; path: string; channel: string }> = [
  { id: 'common', label: 'Общие', path: 'skills/common/', channel: 'коммит в платформу' },
  { id: 'roles', label: 'Ролевые', path: 'skills/roles/', channel: 'коммит в платформу' },
  { id: 'project', label: 'Проектные', path: '<проект>/skills/', channel: 'только /checkpoint' },
]

function parseSkillType(value: string | null): SkillType {
  return value === 'roles' || value === 'project' ? value : 'common'
}

export function SkillsPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [type, setType] = useState<SkillType>(() => parseSkillType(searchParams.get('type')))
  const [selectedName, setSelectedName] = useState<string>(() => searchParams.get('skill') ?? '')
  const [query, setQuery] = useState('')
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState('')
  const { toast } = useToast()
  const queryClient = useQueryClient()

  const commonQuery = useQuery({ queryKey: ['skills', 'common'], queryFn: () => fetchSkills('common') })
  const rolesQuery = useQuery({ queryKey: ['skills', 'roles'], queryFn: () => fetchSkills('roles') })
  const projectQuery = useQuery({ queryKey: ['skills', 'project'], queryFn: () => fetchSkills('project') })
  const activeQuery = type === 'common' ? commonQuery : type === 'roles' ? rolesQuery : projectQuery
  const skills = activeQuery.data ?? []
  const preferredName = type === 'common' ? 'context-management' : type === 'roles' ? 'develop' : ''
  const selected = skills.find((skill) => skill.name === selectedName)
    ?? skills.find((skill) => skill.name === preferredName)
    ?? skills[0]
    ?? null
  const activeType = SKILL_TYPES.find((item) => item.id === type) ?? SKILL_TYPES[0]!
  const filteredSkills = useMemo(() => {
    const needle = query.trim().toLowerCase()
    if (!needle) return skills
    return skills.filter((skill) => [
      skill.name,
      skill.title,
      skill.path,
      skill.desc,
      skill.body,
    ].some((value) => value.toLowerCase().includes(needle)))
  }, [query, skills])

  useEffect(() => {
    if (!selected) {
      setSelectedName('')
      setEditing(false)
      return
    }
    if (selected.name !== selectedName) {
      setSelectedName(selected.name)
      setEditing(false)
      syncRoute(type, selected.name, true)
    }
  }, [selected, selectedName])

  const diffMutation = useMutation({
    mutationFn: () => selected ? diffSkill(selected.name, draft) : Promise.reject(new Error('skill not selected')),
  })
  const confirmMutation = useMutation({
    mutationFn: () => selected ? confirmSkill(selected.name, draft) : Promise.reject(new Error('skill not selected')),
    onSuccess: (result) => {
      toast({
        title: 'Скил записан',
        description: `${result.file}: ${result.commitStatus} · нужен restart/release агентов`,
        tone: 'success',
      })
      setEditing(false)
      diffMutation.reset()
      void queryClient.invalidateQueries({ queryKey: ['skills', 'common'] })
    },
    onError: (error) => {
      toast({
        title: 'Скил не записан',
        description: error instanceof Error ? error.message : 'unknown error',
        tone: 'danger',
      })
    },
  })

  function selectType(next: string) {
    const nextType = next as SkillType
    if (nextType === type) return
    if (!canDiscardDraft()) return
    setType(nextType)
    setSelectedName('')
    setQuery('')
    setEditing(false)
    diffMutation.reset()
    syncRoute(nextType, '', true)
  }

  function selectSkill(skill: SkillDto) {
    if (selected?.name === skill.name) return
    if (!canDiscardDraft()) return
    setSelectedName(skill.name)
    setEditing(false)
    diffMutation.reset()
    syncRoute(type, skill.name, false)
  }

  function startEdit() {
    if (!selected || selected.type !== 'common') return
    setDraft(selected.body)
    setEditing(true)
    diffMutation.reset()
  }

  function refreshAll() {
    void commonQuery.refetch()
    void rolesQuery.refetch()
    void projectQuery.refetch()
  }

  function canDiscardDraft() {
    if (!editing || !selected || draft === selected.body) return true
    if (typeof window === 'undefined') return true
    return window.confirm('Есть несохранённый черновик скила. Сбросить изменения и перейти дальше?')
  }

  function syncRoute(nextType: SkillType, nextSkill: string, replace: boolean) {
    const params = new URLSearchParams()
    params.set('type', nextType)
    if (nextSkill) params.set('skill', nextSkill)
    setSearchParams(params, { replace })
  }

  const stats = [
    { label: 'общих', value: commonQuery.data?.length ?? 0, tone: 'info' },
    { label: 'ролевых', value: rolesQuery.data?.length ?? 0, tone: 'changed' },
    { label: 'проектных', value: projectQuery.data?.length ?? 0, tone: 'success' },
  ] as const

  return (
    <div style={pageStyle}>
      <WorkbenchPanel style={panelStyle}>
        <div style={headerStyle}>
          <div style={{ display: 'grid', gap: 5 }}>
            <span style={eyebrowStyle}>Раздел 4 · Скилы</span>
            <h1 style={titleStyle}>Скилы</h1>
            <span style={subTitleStyle}>источник истины: репозиторий платформы + vault</span>
          </div>
          <div style={headerActionsStyle}>
            <div style={statsStyle}>
              {stats.map((item) => (
                <div key={item.label} style={statStyle}>
                  <span style={{ ...statValueStyle, color: `var(--frm-${item.tone})` }}>{item.value}</span>
                  <span style={statLabelStyle}>{item.label}</span>
                </div>
              ))}
            </div>
            <Button loading={commonQuery.isFetching || rolesQuery.isFetching || projectQuery.isFetching} onClick={refreshAll}>
              Обновить
            </Button>
          </div>
        </div>

        <div style={principleStyle}>
          <span style={accentRailStyle} />
          <p style={principleTextStyle}>
            Системные скилы определяют <strong>поведение платформы</strong>, проектные - <strong>знание проекта</strong>.
          </p>
        </div>

        <div style={formatRowStyle}>
          <span style={microLabelStyle}>Формат</span>
          <Pill leftIcon={<FileIcon />}>Один файл = один скил</Pill>
          <Pill><span style={cyanMonoStyle}>шапка</span> name · description · tools</Pill>
          <Pill><span style={mutedMonoStyle}>тело</span> правила · шаблоны · примеры</Pill>
        </div>

        <ArchitectureDiagram
          commonCount={commonQuery.data?.length ?? 0}
          rolesCount={rolesQuery.data?.length ?? 0}
        />

        <div style={typeCardsStyle}>
          {SKILL_TYPES.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => selectType(item.id)}
              style={{ ...typeCardStyle, ...(type === item.id ? typeCardActiveStyle : null) }}
            >
              <span style={typeCardTitleStyle}>{item.label}</span>
              <span style={monoMutedStyle}>{item.path}</span>
              <span style={channelStyle}>{item.channel}</span>
            </button>
          ))}
        </div>
      </WorkbenchPanel>

      <WorkbenchPanel style={panelStyle}>
        <div style={workbenchHeaderStyle}>
          <div style={{ display: 'grid', gap: 4 }}>
            <h2 style={sectionTitleStyle}>Воркбенч</h2>
            <span style={subTitleStyle}>common редактируются через diff/confirm; roles и project read-only из UI</span>
          </div>
          <div style={currentTypeStyle}>
            <span style={currentTypeTitleStyle}>{activeType.label}</span>
            <span style={monoMutedStyle}>{skills.length} файлов</span>
          </div>
        </div>

        {activeQuery.isLoading ? (
          <Skeleton height={320} />
        ) : activeQuery.isError ? (
          <ErrorBanner
            title="Скилы недоступны"
            description={activeQuery.error instanceof Error ? activeQuery.error.message : 'unknown error'}
            action={<Button onClick={() => void activeQuery.refetch()}>Повторить</Button>}
          />
        ) : skills.length === 0 ? (
          <EmptyState title="Скилы не найдены" description="Backend вернул пустой список для выбранного типа." />
        ) : (
          <div className="skills-workbench-grid" style={workbenchGridStyle}>
            <div style={listStyle}>
              <SearchInput
                aria-label="Поиск скилов"
                controlSize="sm"
                fullWidth={false}
                onChange={(event) => setQuery(event.currentTarget.value)}
                placeholder="Поиск по имени, пути или тексту"
                value={query}
              />
              <div style={listCountStyle}>
                {filteredSkills.length === skills.length ? `${skills.length} скилов` : `${filteredSkills.length} из ${skills.length}`}
              </div>
              {filteredSkills.length === 0 ? (
                <div style={listEmptyStyle}>Нет совпадений по текущему типу.</div>
              ) : filteredSkills.map((skill) => {
                const active = selected?.name === skill.name
                return (
                  <button
                    key={skill.path}
                    type="button"
                    onClick={() => selectSkill(skill)}
                    style={{ ...skillButtonStyle, ...(active ? skillButtonActiveStyle : null) }}
                  >
                    <span style={skillTitleStyle}>{skill.title || skill.name}</span>
                    <span style={monoMutedStyle}>{skill.path}</span>
                    <span style={skillDescStyle}>{skill.desc || 'Без описания'}</span>
                  </button>
                )
              })}
            </div>

            {selected ? (
              <SkillDetail
                skill={selected}
                editing={editing}
                draft={draft}
                setDraft={(next) => {
                  setDraft(next)
                  diffMutation.reset()
                }}
                onEdit={startEdit}
                onCancel={() => {
                  setEditing(false)
                  diffMutation.reset()
                }}
                onDiff={() => diffMutation.mutate()}
                onConfirm={() => confirmMutation.mutate()}
                diff={diffMutation.data}
                diffPending={diffMutation.isPending}
                diffError={diffMutation.error}
                confirmPending={confirmMutation.isPending}
              />
            ) : null}
          </div>
        )}
      </WorkbenchPanel>
    </div>
  )
}

function SkillDetail({
  skill,
  editing,
  draft,
  setDraft,
  onEdit,
  onCancel,
  onDiff,
  onConfirm,
  diff,
  diffPending,
  diffError,
  confirmPending,
}: {
  skill: SkillDto
  editing: boolean
  draft: string
  setDraft: (value: string) => void
  onEdit: () => void
  onCancel: () => void
  onDiff: () => void
  onConfirm: () => void
  diff?: Awaited<ReturnType<typeof diffSkill>>
  diffPending: boolean
  diffError: unknown
  confirmPending: boolean
}) {
  const canEdit = skill.type === 'common'
  const diffLines = useMemo(() => diff ? diffToLines(diff.diff) : [], [diff])
  const [historyOpen, setHistoryOpen] = useState(false)
  const historyQuery = useQuery({
    queryKey: ['skills', skill.type, skill.name, 'history'],
    queryFn: () => fetchSkillHistory(skill.name, skill.type),
    enabled: historyOpen,
  })

  useEffect(() => {
    setHistoryOpen(false)
  }, [skill.name, skill.type])

  return (
    <div style={detailStyle}>
      <div style={detailHeaderStyle}>
        <div style={{ display: 'grid', gap: 4, minWidth: 0 }}>
          <h3 style={detailTitleStyle}>{skill.title || skill.name}</h3>
          <span style={monoMutedStyle}>{skill.path}</span>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          <StatusPill status={skill.channel === 'platform' ? 'changed' : 'ok'} label={skill.channel} />
          <StatusPill status={canEdit ? 'ok' : 'unknown'} label={canEdit ? 'edit -> diff -> confirm' : 'read-only'} />
        </div>
      </div>

      <div style={metaGridStyle}>
        <ReadonlyMeta label="name" value={skill.name} />
        <ReadonlyMeta label="type" value={skill.type} />
        <ReadonlyMeta label="tools" value={skill.tools.length ? skill.tools.join(', ') : 'none'} />
      </div>

      {!editing ? (
        <>
          <div style={actionRowStyle}>
            {canEdit ? (
              <>
                <Button leftIcon={<FrmIcon name="edit" size={14} />} onClick={onEdit}>Редактировать</Button>
                <Button variant="secondary" onClick={() => setHistoryOpen((open) => !open)}>
                  История
                </Button>
              </>
            ) : (
              <>
                <Pill>{skill.type === 'project' ? 'правка только через /checkpoint' : 'ролевой скил read-only из UI'}</Pill>
                <Button variant="secondary" onClick={() => setHistoryOpen((open) => !open)}>
                  История
                </Button>
              </>
            )}
          </div>
          {historyOpen ? <SkillHistory skill={skill} query={historyQuery} /> : null}
          <SkillFullText skill={skill} />
        </>
      ) : (
        <div style={{ display: 'grid', gap: 12 }}>
          <Textarea
            label="Markdown"
            rows={18}
            value={draft}
            onChange={(event) => setDraft(event.currentTarget.value)}
          />
          <div style={actionRowStyle}>
            <Button loading={diffPending} onClick={onDiff}>Сравнить</Button>
            <Button variant="primary" loading={confirmPending} disabled={!diff?.canConfirm} onClick={onConfirm}>
              Подтвердить запись
            </Button>
            <Button variant="ghost" onClick={onCancel}>Отмена</Button>
          </div>
          {diff ? (
            <div style={{ display: 'grid', gap: 10 }}>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <StatusPill status={diff.yamlFrontmatter.ok ? 'ok' : 'danger'} label={diff.yamlFrontmatter.message} />
                <StatusPill status={diff.secretScan.ok ? 'ok' : 'danger'} label={diff.secretScan.message} />
                <StatusPill status={diff.canConfirm ? 'ok' : 'unknown'} label={diff.canConfirm ? 'confirm доступно' : 'нет изменений'} />
              </div>
              <DiffViewer lines={diffLines} title="Diff" compact />
            </div>
          ) : null}
          {diffError ? (
            <ErrorBanner
              title="Diff не построен"
              description={diffError instanceof Error ? diffError.message : 'unknown error'}
            />
          ) : null}
        </div>
      )}
    </div>
  )
}

function SkillHistory({
  skill,
  query,
}: {
  skill: SkillDto
  query: UseQueryResult<Awaited<ReturnType<typeof fetchSkillHistory>>>
}) {
  return (
    <div style={historyBoxStyle}>
      <div style={historyHeaderStyle}>
        <span style={microLabelStyle}>История</span>
        <MonoText>{skill.type}</MonoText>
      </div>
      {query.isLoading ? (
        <Skeleton height={84} />
      ) : query.isError ? (
        <ErrorBanner
          title="История недоступна"
          description={query.error instanceof Error ? query.error.message : 'unknown error'}
        />
      ) : (query.data?.length ?? 0) === 0 ? (
        <span style={historyEmptyStyle}>Git-история для этого скила не найдена.</span>
      ) : (
        <div style={historyListStyle}>
          {query.data?.map((entry) => (
            <div key={`${entry.hash}-${entry.date}`} style={historyRowStyle}>
              <MonoText style={{ color: 'var(--frm-accent)' }}>{entry.hash}</MonoText>
              <span style={historySubjectStyle}>{entry.subject}</span>
              <span style={historyMetaStyle}>{entry.author} · {formatHistoryDate(entry.date)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function ArchitectureDiagram({ commonCount, rolesCount }: { commonCount: number; rolesCount: number }) {
  return (
    <div style={diagramStyle}>
      <div style={diagramCaptionStyle}>Системные vs проектные · где живёт и канал изменения</div>
      <div className="skills-diagram-grid" style={diagramGridStyle}>
        <div style={diagramRepoCardStyle}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
            <span style={diagramIconStyle}><RepoIcon /></span>
            <div style={{ display: 'grid' }}>
              <span style={diagramKickerStyle}>Репозиторий платформы</span>
              <strong>Системные скилы</strong>
            </div>
          </div>
          <div style={diagramRowStyle}><span style={cyanDotStyle} /> <MonoText>skills/common/</MonoText><span style={diagramCountStyle}>общие {commonCount}</span></div>
          <div style={diagramRowStyle}><span style={violetDotStyle} /> <MonoText>skills/roles/</MonoText><span style={diagramCountStyle}>ролевые {rolesCount}</span></div>
          <span style={diagramCommitStyle}>✓ коммит в платформу · подтверждение владельца</span>
        </div>
        <div style={diagramArrowStyle}>
          <span style={diagramArrowLabelStyle}>● системные</span>
          <span style={diagramArrowLineStyle}>→</span>
          <span style={diagramArrowHintStyle}>репозиторий → vault</span>
        </div>
        <div style={diagramVaultStackStyle}>
          <div style={diagramVaultTitleStyle}><VaultIcon /> Хранилища · vault</div>
          {['ai.agents', 'ai.market', 'ai.metrics'].map((name) => (
            <div key={name} style={vaultRowStyle}>
              <div style={{ display: 'grid', gap: 2 }}>
                <strong>vault · {name}</strong>
                <MonoText style={{ color: 'var(--frm-success)' }}>projects/{name}/skills/</MonoText>
              </div>
              <span style={checkpointPillStyle}>/checkpoint</span>
            </div>
          ))}
          <span style={projectOnlyStyle}>↑ ● проектные правка только через <MonoText>/checkpoint</MonoText></span>
        </div>
      </div>
    </div>
  )
}

function SkillFullText({ skill }: { skill: SkillDto }) {
  const lineCount = skill.body.split(/\r?\n/).length
  return (
    <div style={readableBodyStyle}>
      <div style={frontmatterBoxStyle}>
        <MonoText>---</MonoText>
        <MonoText><span style={frontKeyStyle}>name:</span> {skill.name}</MonoText>
        <MonoText><span style={frontKeyStyle}>description:</span> {skill.desc || skill.title}</MonoText>
        <MonoText><span style={frontKeyStyle}>tools:</span> {skill.tools.length ? skill.tools.join(', ') : 'none'}</MonoText>
        <MonoText>---</MonoText>
      </div>

      <div style={sectionBlockStyle}>
        <div style={fullTextHeaderStyle}>
          <div style={microLabelStyle}>Полный текст</div>
          <MonoText>{lineCount} строк</MonoText>
        </div>
        <pre style={codeWellStyle}>{skill.body}</pre>
      </div>
    </div>
  )
}

function ReadonlyMeta({ label, value }: { label: string; value: string }) {
  return (
    <div style={metaCellStyle}>
      <span style={microLabelStyle}>{label}</span>
      <span style={monoStyle}>{value}</span>
    </div>
  )
}

function MonoText({ children, style }: { children: ReactNode; style?: CSSProperties }) {
  return <span style={{ fontFamily: 'var(--frm-font-mono)', fontSize: 12, ...style }}>{children}</span>
}

function diffToLines(diff: string): DiffLine[] {
  return diff.split(/\r?\n/).map((line, index) => {
    if (line.startsWith('+++') || line.startsWith('---') || line.startsWith('@@')) {
      return { id: String(index), kind: 'changed', content: line }
    }
    if (line.startsWith('+')) return { id: String(index), kind: 'added', content: line.slice(1) }
    if (line.startsWith('-')) return { id: String(index), kind: 'removed', content: line.slice(1) }
    return { id: String(index), kind: 'context', content: line.startsWith(' ') ? line.slice(1) : line }
  })
}

function formatHistoryDate(value: string): string {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return date.toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function FileIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <path d="M14 2v6h6" />
    </svg>
  )
}

function RepoIcon() {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <ellipse cx="12" cy="5" rx="7" ry="3" />
      <path d="M5 5v10c0 1.7 3.1 3 7 3s7-1.3 7-3V5" />
      <path d="M5 10c0 1.7 3.1 3 7 3s7-1.3 7-3" />
    </svg>
  )
}

function VaultIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M4 7a2 2 0 0 1 2-2h5l2 2h5a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2z" />
    </svg>
  )
}

const pageStyle: CSSProperties = {
  display: 'grid',
  gap: 18,
}

const panelStyle: CSSProperties = {
  padding: 24,
}

const headerStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: 16,
  flexWrap: 'wrap',
}

const headerActionsStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  flexWrap: 'wrap',
  justifyContent: 'flex-end',
}

const eyebrowStyle: CSSProperties = {
  color: 'var(--frm-accent)',
  fontSize: 10,
  fontWeight: 850,
  letterSpacing: '0.14em',
  textTransform: 'uppercase',
}

const titleStyle: CSSProperties = {
  margin: 0,
  color: 'var(--frm-text)',
  fontSize: 24,
  lineHeight: 1.1,
  fontWeight: 900,
}

const subTitleStyle: CSSProperties = {
  color: 'var(--frm-text-3)',
  fontSize: 12.5,
}

const statsStyle: CSSProperties = {
  display: 'flex',
  gap: 10,
  flexWrap: 'wrap',
}

const statStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
  minHeight: 38,
  padding: '0 13px',
  border: '1px solid var(--frm-line-subtle)',
  borderRadius: 9,
  background: 'var(--frm-surface-2)',
}

const statValueStyle: CSSProperties = {
  fontFamily: 'var(--frm-font-mono)',
  fontSize: 14,
  fontWeight: 750,
}

const statLabelStyle: CSSProperties = {
  color: 'var(--frm-text-3)',
  fontSize: 12,
}

const principleStyle: CSSProperties = {
  display: 'flex',
  gap: 16,
  alignItems: 'stretch',
  marginTop: 18,
  padding: '16px 18px',
  border: '1px solid rgba(56,189,248,0.20)',
  borderRadius: 12,
  background: 'linear-gradient(135deg, rgba(56,189,248,0.08), rgba(99,102,241,0.06))',
}

const accentRailStyle: CSSProperties = {
  width: 4,
  flexShrink: 0,
  borderRadius: 999,
  background: 'linear-gradient(180deg, var(--frm-accent), var(--frm-accent-2))',
}

const principleTextStyle: CSSProperties = {
  margin: 0,
  color: 'var(--frm-text-2)',
  fontSize: 15,
  lineHeight: 1.55,
  fontWeight: 650,
}

const formatRowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 9,
  flexWrap: 'wrap',
  marginTop: 18,
}

const cyanMonoStyle: CSSProperties = {
  color: 'var(--frm-accent)',
  fontFamily: 'var(--frm-font-mono)',
}

const mutedMonoStyle: CSSProperties = {
  color: 'var(--frm-text-3)',
  fontFamily: 'var(--frm-font-mono)',
}

const diagramStyle: CSSProperties = {
  marginTop: 18,
  padding: '20px',
  border: '1px solid var(--frm-line-subtle)',
  borderRadius: 14,
  background: 'var(--frm-workbench-well, #0B1020)',
}

const diagramCaptionStyle: CSSProperties = {
  color: 'var(--frm-text-3)',
  fontSize: 10.5,
  fontWeight: 850,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  marginBottom: 16,
}

const diagramGridStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'minmax(280px,1fr) 168px minmax(280px,1fr)',
  alignItems: 'center',
  gap: 16,
}

const diagramRepoCardStyle: CSSProperties = {
  padding: 18,
  border: '1px solid rgba(56,189,248,0.28)',
  borderRadius: 14,
  background: 'rgba(56,189,248,0.06)',
}

const diagramIconStyle: CSSProperties = {
  width: 30,
  height: 30,
  display: 'grid',
  placeItems: 'center',
  borderRadius: 9,
  background: 'linear-gradient(135deg, var(--frm-accent), var(--frm-accent-2))',
  color: 'var(--frm-text-on-accent)',
}

const diagramKickerStyle: CSSProperties = {
  color: 'var(--frm-accent)',
  fontSize: 10,
  fontWeight: 850,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
}

const diagramRowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  minHeight: 40,
  padding: '0 12px',
  marginTop: 8,
  border: '1px solid var(--frm-line-subtle)',
  borderRadius: 9,
  background: 'var(--frm-workbench-well-2, #080C16)',
}

const cyanDotStyle: CSSProperties = {
  width: 7,
  height: 7,
  borderRadius: '50%',
  background: 'var(--frm-accent)',
}

const violetDotStyle: CSSProperties = {
  ...cyanDotStyle,
  background: 'var(--frm-accent-2)',
}

const diagramCountStyle: CSSProperties = {
  marginLeft: 'auto',
  color: 'var(--frm-text-3)',
  fontSize: 11,
}

const diagramCommitStyle: CSSProperties = {
  display: 'inline-flex',
  marginTop: 12,
  padding: '6px 10px',
  border: '1px solid rgba(56,189,248,0.24)',
  borderRadius: 999,
  background: 'rgba(56,189,248,0.10)',
  color: 'var(--frm-accent)',
  fontSize: 11,
  fontWeight: 800,
}

const diagramArrowStyle: CSSProperties = {
  display: 'grid',
  justifyItems: 'center',
  gap: 6,
  color: 'var(--frm-accent)',
  textAlign: 'center',
}

const diagramArrowLabelStyle: CSSProperties = {
  fontSize: 11,
  fontWeight: 850,
  textTransform: 'uppercase',
}

const diagramArrowLineStyle: CSSProperties = {
  fontSize: 42,
  lineHeight: 1,
  color: 'var(--frm-success)',
}

const diagramArrowHintStyle: CSSProperties = {
  color: 'var(--frm-text-3)',
  fontSize: 11.5,
}

const diagramVaultStackStyle: CSSProperties = {
  display: 'grid',
  gap: 9,
}

const diagramVaultTitleStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  color: 'var(--frm-success)',
  fontSize: 11,
  fontWeight: 850,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
}

const vaultRowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 12,
  padding: '12px 14px',
  border: '1px solid rgba(52,211,153,0.22)',
  borderRadius: 11,
  background: 'rgba(52,211,153,0.06)',
}

const checkpointPillStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  minHeight: 24,
  padding: '0 10px',
  borderRadius: 999,
  border: '1px solid rgba(52,211,153,0.24)',
  background: 'rgba(52,211,153,0.10)',
  color: 'var(--frm-success)',
  fontFamily: 'var(--frm-font-mono)',
  fontSize: 11,
  fontWeight: 800,
}

const projectOnlyStyle: CSSProperties = {
  color: 'var(--frm-success)',
  fontSize: 11.5,
}

const typeCardsStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
  gap: 13,
  marginTop: 18,
}

const typeCardStyle: CSSProperties = {
  display: 'grid',
  gap: 5,
  padding: 16,
  border: '1px solid var(--frm-line-subtle)',
  borderRadius: 14,
  background: 'var(--frm-workbench-well, #0B1020)',
  color: 'var(--frm-text)',
  textAlign: 'left',
  cursor: 'pointer',
}

const typeCardActiveStyle: CSSProperties = {
  borderColor: 'rgba(56,189,248,0.42)',
  background: 'var(--frm-surface-2)',
  boxShadow: 'inset 0 0 0 1px rgba(56,189,248,0.10)',
}

const typeCardTitleStyle: CSSProperties = {
  fontSize: 16,
  fontWeight: 850,
}

const monoMutedStyle: CSSProperties = {
  color: 'var(--frm-text-3)',
  fontFamily: 'var(--frm-font-mono)',
  fontSize: 11.5,
}

const channelStyle: CSSProperties = {
  marginTop: 4,
  color: 'var(--frm-accent)',
  fontSize: 11.5,
  fontWeight: 750,
}

const workbenchHeaderStyle: CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  gap: 16,
  flexWrap: 'wrap',
  marginBottom: 18,
}

const currentTypeStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 10,
  minHeight: 38,
  padding: '0 13px',
  border: '1px solid rgba(56,189,248,0.26)',
  borderRadius: 999,
  background: 'rgba(56,189,248,0.08)',
}

const currentTypeTitleStyle: CSSProperties = {
  color: 'var(--frm-text)',
  fontSize: 13,
  fontWeight: 850,
}

const sectionTitleStyle: CSSProperties = {
  margin: 0,
  fontSize: 18,
  fontWeight: 850,
}

const workbenchGridStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'minmax(220px, 320px) minmax(0, 1fr)',
  gap: 16,
  alignItems: 'start',
}

const listStyle: CSSProperties = {
  display: 'grid',
  gap: 8,
}

const listCountStyle: CSSProperties = {
  color: 'var(--frm-text-3)',
  fontFamily: 'var(--frm-font-mono)',
  fontSize: 11.5,
}

const listEmptyStyle: CSSProperties = {
  padding: '14px 13px',
  border: '1px dashed var(--frm-line-subtle)',
  borderRadius: 11,
  color: 'var(--frm-text-3)',
  fontSize: 12.5,
}

const skillButtonStyle: CSSProperties = {
  display: 'grid',
  gap: 4,
  padding: '12px 13px',
  border: '1px solid var(--frm-line-subtle)',
  borderRadius: 11,
  background: 'var(--frm-workbench-well, #0B1020)',
  color: 'var(--frm-text)',
  textAlign: 'left',
  cursor: 'pointer',
}

const skillButtonActiveStyle: CSSProperties = {
  borderColor: 'rgba(56,189,248,0.36)',
  background: 'rgba(56,189,248,0.08)',
}

const skillTitleStyle: CSSProperties = {
  fontSize: 13.5,
  fontWeight: 850,
}

const skillDescStyle: CSSProperties = {
  color: 'var(--frm-text-3)',
  fontSize: 12,
  lineHeight: 1.4,
}

const detailStyle: CSSProperties = {
  display: 'grid',
  gap: 14,
  minWidth: 0,
}

const detailHeaderStyle: CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  gap: 12,
  flexWrap: 'wrap',
}

const detailTitleStyle: CSSProperties = {
  margin: 0,
  fontSize: 18,
  fontWeight: 900,
}

const metaGridStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
  gap: 1,
  overflow: 'hidden',
  border: '1px solid var(--frm-line-subtle)',
  borderRadius: 11,
  background: 'var(--frm-line-subtle)',
}

const metaCellStyle: CSSProperties = {
  display: 'grid',
  gap: 5,
  padding: '10px 12px',
  background: 'var(--frm-workbench-well, #0B1020)',
  minWidth: 0,
}

const microLabelStyle: CSSProperties = {
  color: 'var(--frm-text-3)',
  fontSize: 10.5,
  fontWeight: 850,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
}

const monoStyle: CSSProperties = {
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  overflowWrap: 'anywhere',
  fontFamily: 'var(--frm-font-mono)',
  fontSize: 12.5,
}

const actionRowStyle: CSSProperties = {
  display: 'flex',
  gap: 10,
  flexWrap: 'wrap',
  alignItems: 'center',
}

const historyBoxStyle: CSSProperties = {
  display: 'grid',
  gap: 10,
  padding: 14,
  border: '1px solid var(--frm-line-subtle)',
  borderRadius: 12,
  background: 'var(--frm-workbench-well, #0B1020)',
}

const historyHeaderStyle: CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  gap: 12,
  alignItems: 'center',
}

const historyListStyle: CSSProperties = {
  display: 'grid',
  gap: 8,
}

const historyRowStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '72px minmax(0, 1fr)',
  gap: 10,
  alignItems: 'center',
  minHeight: 34,
  padding: '7px 9px',
  border: '1px solid var(--frm-line-subtle)',
  borderRadius: 9,
  background: 'var(--frm-workbench-well-2, #080C16)',
}

const historySubjectStyle: CSSProperties = {
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  color: 'var(--frm-text-2)',
  fontSize: 12.5,
}

const historyMetaStyle: CSSProperties = {
  gridColumn: '2 / 3',
  color: 'var(--frm-text-3)',
  fontSize: 11.5,
}

const historyEmptyStyle: CSSProperties = {
  color: 'var(--frm-text-3)',
  fontSize: 12.5,
}

const readableBodyStyle: CSSProperties = {
  display: 'grid',
  gap: 14,
}

const frontmatterBoxStyle: CSSProperties = {
  display: 'grid',
  gap: 5,
  padding: 14,
  border: '1px solid var(--frm-line-subtle)',
  borderRadius: 12,
  background: 'var(--frm-workbench-well, #0B1020)',
}

const frontKeyStyle: CSSProperties = {
  color: 'var(--frm-accent-2)',
}

const sectionBlockStyle: CSSProperties = {
  display: 'grid',
  gap: 9,
}

const fullTextHeaderStyle: CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  gap: 12,
  alignItems: 'center',
}

const codeWellStyle: CSSProperties = {
  margin: 0,
  minHeight: 72,
  padding: '12px 13px',
  border: '1px solid var(--frm-line-subtle)',
  borderRadius: 9,
  background: 'var(--frm-workbench-well-2, #080C16)',
  color: 'var(--frm-text-2)',
  fontFamily: 'var(--frm-font-mono)',
  fontSize: 12,
  lineHeight: 1.6,
  whiteSpace: 'pre-wrap',
  overflow: 'auto',
}
